import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CalendarClock, Check, Clock, Trash2, UserX } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  deleteSignup,
  getEvent,
  listOccurrences,
  listSignups,
  updateSignup,
  type EventOccurrenceDto,
  type SignupDto,
} from '../../lib/events-api'

const occStatusVariant: Record<string, 'success' | 'neutral' | 'danger'> = {
  scheduled: 'success',
  completed: 'neutral',
  canceled: 'danger',
}

const signupStatusVariant: Record<string, 'success' | 'gold' | 'neutral' | 'danger' | 'secondary'> =
  {
    registered: 'success',
    waitlisted: 'gold',
    attended: 'secondary',
    no_show: 'neutral',
    canceled: 'danger',
  }

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function OccurrencesCard({
  occurrences,
  capacity,
}: {
  occurrences: EventOccurrenceDto[]
  capacity: number | null
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Occurrences</CardTitle>
        <Badge variant="secondary">{occurrences.length}</Badge>
      </CardHeader>
      <CardContent>
        {occurrences.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted">
            No occurrences scheduled. Single-session events use the event date.
          </p>
        ) : (
          <ul className="space-y-2">
            {occurrences.map((o) => {
              const cap = o.capacityOverride ?? capacity
              return (
                <li
                  key={o.id}
                  className="flex items-center justify-between gap-3 rounded-lg bg-card px-3 py-2.5"
                >
                  <div className="flex items-center gap-3">
                    <CalendarClock className="size-4 text-secondary" />
                    <div>
                      <div className="text-sm font-medium">{formatDateTime(o.startsAt)}</div>
                      {o.endsAt && (
                        <div className="text-xs text-dim">until {formatDateTime(o.endsAt)}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-dim">
                      {cap != null ? `${o.seatsTaken}/${cap}` : `${o.seatsTaken} seats`}
                    </span>
                    <Badge variant={occStatusVariant[o.status] ?? 'neutral'} className="capitalize">
                      {o.status}
                    </Badge>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function SignupRow({ eventId, signup }: { eventId: string; signup: SignupDto }) {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['event', eventId, 'signups'] })
    void queryClient.invalidateQueries({ queryKey: ['event', eventId] })
  }

  const setStatus = useMutation({
    mutationFn: (status: SignupDto['status']) => updateSignup(eventId, signup.id, { status }),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: () => deleteSignup(eventId, signup.id),
    onSuccess: invalidate,
  })

  const name = signup.personName ?? signup.guestName ?? 'Guest'
  const busy = setStatus.isPending || remove.isPending

  return (
    <tr className="border-b border-border-soft transition-colors last:border-0 hover:bg-card-hover">
      <td className="px-5 py-3">
        <span className="font-medium">{name}</span>
        {signup.guestEmail && <span className="ml-2 text-xs text-dim">{signup.guestEmail}</span>}
      </td>
      <td className="px-5 py-3 text-muted">{signup.quantity}</td>
      <td className="px-5 py-3">
        <Badge variant={signupStatusVariant[signup.status] ?? 'neutral'} className="capitalize">
          {signup.status.replace('_', ' ')}
        </Badge>
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-1">
          {signup.status !== 'attended' && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Mark attended"
              disabled={busy}
              onClick={() => setStatus.mutate('attended')}
            >
              <Check />
            </Button>
          )}
          {signup.status === 'waitlisted' && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Promote to registered"
              disabled={busy}
              onClick={() => setStatus.mutate('registered')}
            >
              <Clock />
            </Button>
          )}
          {signup.status !== 'no_show' && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Mark no-show"
              disabled={busy}
              onClick={() => setStatus.mutate('no_show')}
            >
              <UserX />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Remove signup"
            disabled={busy}
            onClick={() => remove.mutate()}
          >
            <Trash2 />
          </Button>
        </div>
      </td>
    </tr>
  )
}

export function EventDetail() {
  const { id = '' } = useParams<{ id: string }>()

  const eventQuery = useQuery({
    queryKey: ['event', id],
    queryFn: () => getEvent(id),
    enabled: !!id,
  })
  const occQuery = useQuery({
    queryKey: ['event', id, 'occurrences'],
    queryFn: () => listOccurrences(id),
    enabled: !!id,
  })
  const signupsQuery = useQuery({
    queryKey: ['event', id, 'signups'],
    queryFn: () => listSignups(id),
    enabled: !!id,
  })

  if (eventQuery.isLoading) {
    return (
      <Card className="flex items-center justify-center gap-2 py-20 text-muted">
        <Spinner /> Loading event…
      </Card>
    )
  }
  if (eventQuery.isError || !eventQuery.data) {
    return (
      <Card className="space-y-2 py-20 text-center">
        <p className="text-sm text-danger">Couldn’t load this event.</p>
        <Link
          to="/app/events"
          className="text-xs text-secondary underline-offset-4 hover:underline"
        >
          Back to events
        </Link>
      </Card>
    )
  }

  const event = eventQuery.data
  const signups = signupsQuery.data ?? []

  return (
    <div className="space-y-6">
      <Link
        to="/app/events"
        className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-fg"
      >
        <ArrowLeft className="size-4" /> Events
      </Link>

      <PageHeader
        title={event.name}
        description={event.description ?? formatDateTime(event.startsAt)}
        actions={
          <>
            <Badge variant="secondary" className="capitalize">
              {event.kind}
            </Badge>
            <Badge variant="neutral" className="capitalize">
              {event.visibility}
            </Badge>
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="p-5">
            <div className="text-xs text-dim">Starts</div>
            <div className="mt-1 text-sm font-medium">{formatDateTime(event.startsAt)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-xs text-dim">Registered</div>
            <div className="mt-1 font-display text-2xl font-bold">
              {event.registeredCount}
              {event.capacity != null && (
                <span className="text-base text-dim"> / {event.capacity}</span>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-xs text-dim">Waitlisted</div>
            <div className="mt-1 font-display text-2xl font-bold">{event.waitlistedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-xs text-dim">Registration</div>
            <div className="mt-1 text-sm font-medium">
              {event.registrationEnabled ? (
                <span className="text-success">Open</span>
              ) : (
                <span className="text-muted">Off</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <OccurrencesCard occurrences={occQuery.data ?? []} capacity={event.capacity} />

      <Card className="overflow-hidden p-0">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Roster</CardTitle>
          <Badge variant="secondary">{signups.length}</Badge>
        </CardHeader>
        {signupsQuery.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted">
            <Spinner /> Loading roster…
          </div>
        ) : signups.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted">No signups yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Qty</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {signups.map((s) => (
                  <SignupRow key={s.id} eventId={id} signup={s} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
